/* The notices makers' packs owe their sources (es-homebrew-engine.md §7), composed by esback's
 * EsNotices: a pack's own notice, then each ancestor's, oldest first, each once.
 *
 * A built-in ancestor -- the SRD, SRD 5.1, the BFRD -- comes as its work key, not as text, and is
 * drawn here from EsLicenses' LICENSED_WORKS: the one copy of each of those notices, which jest
 * pins byte for byte (test/EsPackNotices.test.jsx pins the composed SRD notice to it). A maker's
 * notice is their text, verbatim, URLs made links and nothing else changed.
 *
 * Two licenses add a shape of their own. ORC (§III): the ORC Notice before the makers' attribution,
 * reserved material, and expressly designated statements. OGL 1.0a (§6, §10, §15): the license's
 * own COPYRIGHT NOTICE lines and the pack's chain after them, with the full text beside, as §10
 * asks of every copy.
 *
 * EsLicenses stays bare and fetch-free; this is drawn on the Legal hub and on a pack's page,
 * both of which already read esback, and is nothing while rolloutEsHomebrewEngine is off.
 */

import { useEffect, useState } from 'react';
import { useEsHomebrewEngineFlag } from '../../common/EsFlags';
import { useEsStrings } from '../../common/EsStrings';
import { API_PACK_NOTICES } from '../../common/EsConstants';
import { esRead } from '../../common/EsFetch';
import { esNoticeParts, LICENSED_WORKS } from './EsLicenses';
import { OGL_COPYRIGHT_HEADING, OGL_COPYRIGHT_NOTICE, OGL_END, OGL_PREAMBLE, OGL_SECTIONS, OGL_TITLE } from './EsOgl';

const LINK = 'underline decoration-dotted hover:text-accent-hover';
const ORC_URL = 'https://downloads.paizo.com/ORC_License_FINAL.pdf';

/** A notice line's text: a maker's verbatim, or a built-in work's from LICENSED_WORKS. */
export function noticeText( pLine ) {
    if ( pLine?.work ) return LICENSED_WORKS.find( ( pWork ) => pWork.key === pLine.work )?.notice ?? null;
    return pLine?.text ?? null;
}

// esNoticeParts alternates text and link; each run is keyed by where it starts in the notice.
function noticeRuns( pText ) {
    let aAt = 0;
    return esNoticeParts( pText ).map( ( pPart, pIx ) => {
        const aRun = { at: aAt, text: pPart, link: pIx % 2 === 1 };
        aAt += pPart.length;
        return aRun;
    } );
}

function NoticeText( { id, text } ) {
    return (
        <p data-testid={ id } className="mb-3 whitespace-pre-line rounded-sm border border-edge p-5 text-base leading-8">
            { noticeRuns( text ).map( ( pRun ) =>
                pRun.link
                    ? <a key={ pRun.at } href={ pRun.text } target="_blank" rel="noreferrer" className={ LINK }>{ pRun.text }</a>
                    : <span key={ pRun.at }>{ pRun.text }</span>
            ) }
        </p>
    );
}

// The license in full with this pack's §15 chain in place of any other: §10's copy with every copy.
function OglText( { id, chain } ) {
    return (
        <details data-testid={ id } className="mb-3 rounded-sm border border-edge p-5 text-sm leading-7">
            <summary className="cursor-pointer font-semibold">{ OGL_TITLE }</summary>
            <p className="mt-4 mb-4">{ OGL_PREAMBLE }</p>
            <ol className="mb-4 list-decimal space-y-2 pl-6">
                { OGL_SECTIONS.map( ( pText ) => <li key={ pText.slice( 0, 40 ) }>{ pText }</li> ) }
                <li>
                    { OGL_COPYRIGHT_HEADING }
                    { [ ...new Set( [ ...OGL_COPYRIGHT_NOTICE, ...chain ] ) ].map( ( pLine ) =>
                        <p key={ pLine } className="mt-2" data-testid={ id + '-line' }>{ pLine }</p> ) }
                </li>
            </ol>
            <p>{ OGL_END }</p>
        </details>
    );
}

/** One pack's composed notice, as esback's EsPackNotice carries it. */
export function EsPackNoticeBlock( { notice, id } ) {
    const t = useEsStrings();
    if ( !notice ) return null;
    return (
        <div data-testid={ id } className="mb-8">
            <h5 data-testid={ id + '-heading' } className="mb-2 text-base font-bold text-heading">
                { notice.name }{ notice.license && <span className="ml-2 text-sm font-normal text-muted">{ notice.license }</span> }
            </h5>
            { notice.orc &&
                <p data-testid={ id + '-orc' } className="mb-3 text-base leading-8">
                    { /* es-copy: literal -- the ORC License's own label and notice */ }
                    <span className="font-semibold">ORC Notice:</span> { notice.orc } <a href={ ORC_URL } target="_blank" rel="noreferrer" className={ LINK }>{ ORC_URL }</a>
                </p> }
            { ( notice.notices ?? [] ).map( ( pLine, pIx ) => {
                const aText = noticeText( pLine );
                return aText && <NoticeText key={ aText } id={ id + '-notice-' + pIx } text={ aText } />;
            } ) }
            { notice.copyright && <OglText id={ id + '-ogl' } chain={ notice.copyright } /> }
            { ( notice.notices ?? [] ).length === 0 && !notice.orc && !notice.copyright &&
                <p data-testid={ id + '-none' } className="mb-3 text-base leading-8 text-muted">
                    { t( 'legal-packs-no-notice', 'Its maker gives no notice to show.' ) }</p> }
        </div>
    );
}

/**
 * A pack page's notice, read for that pack and read again on the codex's `version`: the Publish
 * dialog saves the pack's own notice without leaving the page, and the page shows what it saved.
 */
export function EsPackNotice( { packId, version } ) {
    const homebrew = useEsHomebrewEngineFlag();
    const [ notice, setNotice ] = useState( null );

    // Another pack's notice goes at once; this pack's stays drawn while it is read again.
    useEffect( () => { setNotice( null ); }, [ packId ] );

    useEffect( () => {
        if ( !homebrew || packId == null ) return undefined;
        let aStale = false;
        esRead( API_PACK_NOTICES + '/' + packId )
            .then( ( pNotice ) => { if ( !aStale ) setNotice( pNotice ); } )
            .catch( () => { if ( !aStale ) setNotice( null ); } );
        return () => { aStale = true; };
    }, [ homebrew, packId, version ] );

    const hasAny = notice && ( ( notice.notices?.length ?? 0 ) > 0 || notice.orc || notice.copyright );
    return hasAny ? <EsPackNoticeBlock notice={ notice } id="es-codex-library-page-notice" /> : null;
}

/** The Legal hub's section: the reader's installed packs' notices, then every other Library pack's. */
export default function EsPackNotices() {
    const t = useEsStrings();
    const homebrew = useEsHomebrewEngineFlag();
    const [ notices, setNotices ] = useState( null );

    useEffect( () => {
        setNotices( null );
        if ( !homebrew ) return undefined;
        let aLive = true;
        esRead( API_PACK_NOTICES )
            .then( ( pNotices ) => { if ( aLive ) setNotices( pNotices ); } )
            // A legal section that cannot be read is left out, not drawn empty as if nothing were owed.
            .catch( () => { if ( aLive ) setNotices( null ); } );
        return () => { aLive = false; };
    }, [ homebrew ] );

    const aInstalled = notices?.installed ?? [];
    const aLibrary = notices?.library ?? [];
    if ( aInstalled.length + aLibrary.length === 0 ) return null;

    const group = ( pId, pTitle, pPacks ) => pPacks.length > 0 && (
        <div data-testid={ 'es-legal-packs-' + pId }>
            <p className="mb-3 esLabel">{ pTitle }</p>
            { pPacks.map( ( pNotice ) =>
                <EsPackNoticeBlock key={ pNotice.packId } notice={ pNotice } id={ 'es-legal-pack-' + pNotice.packId } /> ) }
        </div>
    );
    return (
        <div data-testid="es-legal-packs" className="mb-10">
            <h4 data-testid="es-legal-packs-heading" className="mb-3 text-lg font-bold text-heading">
                { t( 'legal-packs-heading', 'Packs by makers' ) }
            </h4>
            <p data-testid="es-legal-packs-blurb" className="mb-3 text-base leading-8">
                { t( 'legal-packs-blurb', 'Packs that makers publish to the Library carry the notices their text owes its sources, composed from every pack they build on.' ) }
            </p>
            { group( 'installed', t( 'legal-packs-installed', 'Packs you installed' ), aInstalled ) }
            { group( 'library', t( 'legal-packs-library', 'In the Library' ), aLibrary ) }
        </div>
    );
}
